import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useUpdateBrainImage } from "@/api/mutations/brainImages";
import {
  validateUpdateBrainImage,
  type UpdateBrainImageInput,
  type ValidationError,
} from "@/api/schemas/brainImage";
import type { BrainImage } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: BrainImage;
}

export function UpdateBrainImageDialog({ open, onOpenChange, image }: Props) {
  const [form, setForm] = useState<UpdateBrainImageInput>({
    name: image.name,
    description: image.description,
    imageRef: image.imageRef,
    digest: image.digest ?? "",
  });
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useUpdateBrainImage();

  useEffect(() => {
    if (open) {
      setForm({
        name: image.name,
        description: image.description,
        imageRef: image.imageRef,
        digest: image.digest ?? "",
      });
      setErrors([]);
      setServerError(null);
    }
  }, [open, image.name, image.description, image.imageRef, image.digest]);

  function fieldError(f: ValidationError["field"]) {
    return errors.find((e) => e.field === f)?.message;
  }

  function handleSubmit() {
    const v = validateUpdateBrainImage(form);
    setErrors(v);
    setServerError(null);
    if (v.length > 0) return;

    const digestTrimmed = form.digest.trim();
    mutation.mutate(
      {
        id: image.id,
        name: form.name,
        description: form.description,
        imageRef: form.imageRef,
        digest: digestTrimmed.length > 0 ? digestTrimmed : null,
      },
      {
        onSuccess: () => {
          toast.success("Brain-Image aktualisiert");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Update fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="update-brainimage-dialog">
        <DialogHeader>
          <DialogTitle>Brain-Image bearbeiten</DialogTitle>
          <DialogDescription>
            Änderungen wirken erst bei nächster Brain-Container-Erstellung (kein laufender Restart).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="biu-name">Name</Label>
            <Input
              id="biu-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="biu-field-name"
            />
            {fieldError("name") && <div className="text-xs text-red">{fieldError("name")}</div>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="biu-imageref">Image-Ref</Label>
            <Input
              id="biu-imageref"
              value={form.imageRef}
              onChange={(e) => setForm({ ...form, imageRef: e.target.value })}
              className="font-mono text-xs"
              data-testid="biu-field-imageref"
            />
            {fieldError("imageRef") && (
              <div className="text-xs text-red">{fieldError("imageRef")}</div>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="biu-digest">Digest (optional, leer = clear)</Label>
            <Input
              id="biu-digest"
              value={form.digest}
              onChange={(e) => setForm({ ...form, digest: e.target.value })}
              className="font-mono text-xs"
              data-testid="biu-field-digest"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="biu-description">Description</Label>
            <Textarea
              id="biu-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="biu-field-description"
            />
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="biu-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="biu-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="biu-submit">
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
